import React, { useEffect } from 'react';
import 'styles/home.css';
import 'styles/deep-look.css';

import { SiteFooter, SiteNav } from 'views/SiteChrome.js';
import { DeepLookButton, DeepLookStatus, useDeepLookStatus } from 'views/DeepLookCta.js';

/* The Deep Look offer page.
 *
 * Built against two reference pages Mike chose, Serus and Popcorn. The approach
 * and the rules it has to keep are in design/deep-look-page.md rather than here.
 */

/* Illustrative, and labelled as such on the page.
 *
 * It has to do three things, and the earlier versions each missed one. It opens
 * with a question only the owner can answer, because that's what separates this
 * from a plain code review. The finding has to be one a plain review wouldn't
 * produce: "add a timeout" is what any agent says, whereas "de-identify rather
 * than delete" is the methodology's actual advice. And it comes from data
 * handling, a part of the review none of the four real findings below come
 * from, so it can't be mistaken for one of them written up. */
const CHAT = [
    { who: 'agent', text: 'Before I look at accounts: when somebody asks you to delete their data, what should happen?' },
    { who: 'you', text: 'We delete their account. There’s a button for it.' },
    { who: 'agent', text: 'I checked. The button removes the user row, but their uploads, their order history and the copies in your analytics all stay. Your privacy policy says you delete everything.' },
    { who: 'finding' }
];

/* All eight, because the buyer is paying for the method and could previously
   see three-eighths of its outline. Each line is what that part of the
   methodology actually examines, written plainly rather than lifted as a quote,
   so none of them reads like the tidy AI aphorism this page is meant to be the
   alternative to. */
const AREAS = [
    ['Viability and fit', 'Whether the person building it is close to the problem, and whether the plan holds together when somebody asks about it.'],
    ['Security', 'Who can reach what, checked against how a well-built product gates it. Every finding needs a person to confirm it.'],
    ['Scale and infrastructure', 'Whether growing means adding capacity or rewriting, and whether the spend matches the traffic you actually have.'],
    ['Payments', 'Whether your records and your payment processor agree, what happens when a card fails, and who decides what a returning customer pays.'],
    ['Data handling', 'Whether the code can actually do what your privacy policy promises, including removing somebody when they ask.'],
    ['Third-party services', 'What it would take to leave a vendor if its price, quality or terms changed: an endpoint change, or a rewrite.'],
    ['Operations', 'What a customer sees when something breaks, whether anybody on your side is told, and whether you could reproduce it.'],
    ['Maintainability', 'Whether somebody, including another AI agent, could change the code later without breaking things they didn’t know were connected.']
];

const CATCHES = [
    {
        title: 'Alerts that never fire',
        body: 'The alert meant to fire when money was left unprotected had never once reached the owner’s phone. It checked with a quick local test rather than assuming.',
        tag: 'Confirmed with a local test'
    },
    {
        title: 'Your records drift from the money',
        body: 'The system only ever checked what it already knew about. One result was a position left open and unprotected across an entire weekend.',
        tag: 'The root of four earlier problems'
    },
    {
        title: 'Nothing watching what matters',
        body: 'It said the setup was the right size and the spend was fair. The real exposure was that if the process handling money stopped, nothing would notice.',
        tag: 'Went against the usual advice'
    },
    {
        title: 'Scripts that shipped with the app',
        body: 'Ninety-one one-off scripts deployed with no protection, some able to take irreversible actions. It couldn’t confirm they were reachable, so it said exactly what to check.',
        tag: 'Couldn’t confirm it, and said so'
    }
];

const AUDIENCE = [
    'Shipping fast with AI',
    'Stuck before launch',
    'Handed the project at work',
    'Making the case to your boss',
    'Still working the idea out',
    'Live with paying customers'
];

/* Each item says exactly what it can guarantee and no more. An earlier version
   headed one of these "It looks, it doesn't change" when all the methodology
   can do is tell the agent not to; the heading has to match the promise. */
const TRUST = [
    ['A report only if you send one', 'It’s yours. Nothing gets published without your say.'],
    ['Security is flagged, never cleared', 'It won’t ever tell you that you’re safe.'],
    ['Told not to change anything', 'It’s instructed not to edit while it runs. Your agent’s own permissions still decide what it can do.'],
    ['Two ways to a refund', 'Never ran it: refunded, nothing to prove. Ran it and it couldn’t produce a read on your project: send us the transcript.']
];

const QUESTIONS = [
    {
        q: 'What is it, exactly?',
        a: 'A connector you add to your AI agent. When you ask for a deep look, it gives it the discovery methodology Dura Labs uses at the start of every engagement: eight areas of review, what to look for in each, and a rule for what matters most. Your agent asks about the parts the code can’t tell it, reads the parts it can, and ranks what it finds by how much it matters to your product.',
        open: true
    },
    {
        q: 'Which tools does it work in?',
        a: 'Any agent that supports MCP connectors, which includes Claude, ChatGPT and Grok. You’ll need a plan that allows custom connectors. Where your agent can read your files, like Claude Code, it goes through the repository directly. Where it can’t, it reviews whatever you share or describe, including a plan with no code yet. We’ve run it ourselves in Claude and Claude Code, so that’s the setup we can speak to first-hand.'
    },
    {
        q: 'What exactly do you see?',
        a: 'Which parts of the review your agent opened, and the one sentence it wrote about why, something like “opened payments because the project bills subscriptions through Stripe.” It’s tied to the email you signed in with. Never your code, unless you choose to send us a report.'
    },
    {
        q: 'Will it run anything?',
        a: 'It reads your code, and it may run small checks to confirm a finding rather than assume it, the way it would for any other task. What it’s allowed to run is up to your agent’s permission settings. It’s told not to change anything while it looks.'
    },
    {
        q: 'Will it try to sell me something?',
        a: 'Once, at the end. It suggests talking the findings through with Dura Labs before making big changes, and it says that once. You’re under no obligation, and the findings are yours either way.'
    },
    {
        q: 'Does it depend on which model I use?',
        a: 'Yes. The methodology tells your agent what to look for and how to rank it, but your agent still does the reading, so a more capable model follows it more thoroughly. Use the most capable model your plan gives you.'
    },
    {
        q: 'Can I share the report?',
        a: 'Yes. It’s written to stand on its own, so you can forward it to somebody who’s never seen this page, including whoever signs off on bringing in help.'
    },
    {
        q: 'Why thirty days?',
        a: 'So you can run it, fix what it finds, and run it again to check the fixes held. After thirty days the connector stops answering until you buy more time. Nothing charges automatically.'
    },
    {
        q: 'What does a run cost me in usage?',
        a: 'It runs inside your agent, so it comes out of your own API credits or plan. We haven’t measured it across enough projects yet to give you a fair number, and we’d rather not guess. It’s a substantial run, so start it with room left in your plan.'
    },
    {
        q: 'Would my report ever be published?',
        a: 'Not unless you asked us to. The one on this site is up because its owner agreed to it.'
    },
    {
        q: 'What if I run it and it doesn’t tell me anything useful?',
        a: 'That’s the second way to a refund. Email us the transcript of the run, trimmed of anything you’d rather keep. If it couldn’t produce a read on your project, we’ll refund you. It doesn’t apply if you’ve used it on another project and got a useful read there.'
    }
];

const ChatDemo = () => (
    <figure className={'dlk-chat'}>
        <div className={'dlk-chat-panel'}>
            {CHAT.map((line, index) => {

                if(line.who === 'finding') {
                    return (
                        <div className={'dlk-msg is-agent'} key={index}>
                            <img alt={''} className={'dlk-avatar'} src={'/mcp-icon.png'} />
                            <div className={'dlk-msg-body'}>
                                <span className={'dlk-msg-name'}>Your agent</span>
                                <div className={'dlk-finding-card'}>
                                    <span className={'dlk-finding-rank'}>Ranked first · High confidence</span>
                                    <b>Deletion doesn’t do what your policy says</b>
                                    <span>That gap is where the exposure sits. De-identify the account everywhere its data lives, rather than deleting one row.</span>
                                </div>
                            </div>
                        </div>
                    )
                }

                const agent = line.who === 'agent';
                return (
                    <div className={agent ? 'dlk-msg is-agent' : 'dlk-msg is-you'} key={index}>
                        {agent && <img alt={''} className={'dlk-avatar'} src={'/mcp-icon.png'} />}
                        <div className={'dlk-msg-body'}>
                            <span className={'dlk-msg-name'}>{agent ? 'Your agent' : 'You'}</span>
                            <p>{line.text}</p>
                        </div>
                    </div>
                )
            })}
        </div>
        <figcaption>An example run. The four findings further down are from a real one.</figcaption>
    </figure>
);

/* A complete finding from the published report, verbatim, so the reader sees
   the whole shape of one: what it found, the reasoning, what it couldn't tell,
   and what to do. The previous excerpt pointed at sections the reader couldn't
   see and quoted a line built on "isn't X, it's Y". This one stands on its own.
   The dash is the report's, not the page's; the report is quoted as written. */
const ReportPage = () => (
    <a className={'dlk-doc'} href={'/deep-look-report'}>
        <div className={'dlk-doc-head'}>
            <span>Deep Look · Futures-AI</span>
            <span>17 Sep 2026</span>
        </div>
        <span className={'dlk-doc-section'}>4.7 The push channel itself may be pointed at the wrong Apple server</span>
        <p>
            If your phone runs a TestFlight or development build, sandbox is correct and this is
            nothing. If it runs an App Store build, the device token is a production token and
            sandbox APNS will reject it — meaning every alert in the system fails silently, not
            just the three in 4.2.
        </p>
        <p>
            I can’t tell which build you’re running. Worth thirty seconds to confirm, because
            everything else in the alerting story depends on it.
        </p>
        <span className={'dlk-doc-go'}>Read the full report <span className={'dl-arrow'}>→</span></span>
    </a>
);

const Closing = ({ utils }) => {

    const status = useDeepLookStatus();

    if(status && status.pass) {
        return (
            <div className={'dlk-price-card'}>
                <h2 className={'dl-display'}>Your access is still running.</h2>
                <p>If you came back to connect it again, that’s on the next page.</p>
                <a className={'dl-btn'} href={'/deep-look/ready'}>
                    Connect it <span className={'dl-arrow'}>→</span>
                </a>
            </div>
        )
    }

    /* No heading on this side. The steps column has one, and a second heading
       across the rule from it, at a different size and a different height,
       read as the two halves disagreeing. The number carries this side on its
       own. */
    return (
        <div className={'dlk-price-card'}>
            <span className={'dlk-price'}>$199</span>
            <span className={'dlk-price-sub'}>Thirty days, charged once, for one account.</span>
            <DeepLookButton label={'Start a Deep Look'} utils={utils} />
            <span className={'dlk-price-note'}>Sign in with your email, then pay.</span>
        </div>
    )
}

const DeepLook = ({ utils }) => {

    useEffect(() => {
        document.body.classList.add('dl-body');

        /* The tab only. The site has no per-page title, so every page showed the
           studio tagline, which is useless in a tab or a bookmark. Link previews
           are a separate problem: crawlers don't run this, so they still see the
           title in index.html until the server sets it for this route. */
        const previous = document.title;
        document.title = 'The Deep Look · Dura Labs';

        return () => {
            document.body.classList.remove('dl-body');
            document.title = previous;
        }
    }, []);

    return (
        <DeepLookStatus utils={utils}>
            <div className={'dl dlk'}>
                {/* variant="offer" puts this page's own ask in the nav in place
                    of the Quick Look, so there's one offer and it's always in
                    reach. */}
                <SiteNav utils={utils} variant={'offer'} />

                <section className={'dlk-hero'}>
                    <div className={'dl-wrap dlk-hero-inner'}>
                        <span className={'dlk-kicker'}>The Deep Look</span>
                        <h1 className={'dl-display'}>An engineer’s review.<br />Run by your own AI.</h1>
                        <p className={'dlk-hero-sub'}>
                            It asks the questions your coding tool was never told to ask, and tells you
                            what to fix first. Unlimited runs for 30 days.
                        </p>
                        <div className={'dlk-actions'}>
                            <DeepLookButton label={'Start a Deep Look'} utils={utils} />
                            <a className={'dlk-quiet'} href={'/deep-look-report'}>Read a real report</a>
                        </div>
                        <p className={'dlk-hero-note'}>Works in any AI agent that supports MCP.</p>
                    </div>
                    <div className={'dl-wrap'}>
                        <ChatDemo />
                    </div>
                </section>

                <section className={'dlk-areas'}>
                    <div className={'dl-wrap'}>
                        <header className={'dlk-head'}>
                            <h2 className={'dl-display'}>What it looks at.</h2>
                            <p>
                                Eight areas. It opens the ones that apply to your product, and ranks what it
                                finds across all of them by how much it matters.
                            </p>
                        </header>
                        <ol className={'dlk-area-grid'}>
                            {AREAS.map(([name, line], index) => (
                                <li key={name}>
                                    <span className={'dlk-area-n'}>{String(index + 1).padStart(2, '0')}</span>
                                    <h3>{name}</h3>
                                    <p>{line}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Straight after the eight areas, so the reader sees the method
                    and then who wrote it, with a piece of one of the areas they
                    just read. It used to sit directly above the close, where its
                    dark excerpt card stacked on the dark price card at nearly the
                    same size and a few pixels out of line, and the two read as a
                    layout repeating itself by mistake.

                    Credited to the studio rather than to a person. */}
                <section className={'dlk-author'}>
                    <div className={'dl-wrap dlk-author-grid'}>
                        <div>
                            <h2 className={'dl-display'}>Written by Dura&nbsp;Labs.</h2>
                            <p>
                                Dura Labs is a small, senior studio with over a decade of production
                                engineering behind it, including a lead-management platform that has run in
                                production for years and five platforms that grew out of one manual process.
                                This is the discovery work that opens every engagement, written down so your
                                agent can run it.
                            </p>
                            <a className={'dlk-quiet dlk-author-more'} href={'/#why'}>More about Dura Labs</a>
                        </div>
                        <div>
                            <p className={'dlk-author-note'}>Here’s part of area 04, as your agent receives it.</p>
                            <figure className={'dlk-file'}>
                            <figcaption>
                                <span className={'dlk-file-dot'} />
                                branch-d-payments-and-money-flow.md
                            </figcaption>
                            <blockquote>
                                A coding tool will happily wire up a subscription without ever implementing
                                the webhook listener that keeps the state honest, because nothing in the
                                request told it to. Go and look for the listener.
                            </blockquote>
                            </figure>
                        </div>
                    </div>
                </section>

                <section className={'dlk-facts'}>
                    <div className={'dl-wrap dlk-facts-grid'}>

                        <article className={'dlk-fact is-a'}>
                            <div className={'dlk-object'}>
                                <span className={'dlk-object-label'}>Thirty days</span>
                                <span className={'dlk-object-big'}>$199</span>
                                <span className={'dlk-pill'}>✓ Charged once</span>
                            </div>
                            <h3>One price</h3>
                            <p>Unlimited runs for thirty days, on any number of projects.</p>
                        </article>

                        <article className={'dlk-fact is-b'}>
                            <div className={'dlk-object'}>
                                <span className={'dlk-object-label'}>What reaches Dura Labs</span>
                                <ul className={'dlk-seen'}>
                                    <li className={'is-yes'}>Which review areas ran</li>
                                    <li className={'is-yes'}>A one-line reason for each</li>
                                    <li className={'is-no'}>Your code</li>
                                </ul>
                            </div>
                            <h3>Your code stays yours</h3>
                            <p>Unless you send a report, those two lines are all a run sends us.</p>
                        </article>

                        <article className={'dlk-fact is-c'}>
                            <div className={'dlk-object'}>
                                <div className={'dlk-connector'}>
                                    <img alt={''} src={'/mcp-icon.png'} />
                                    <span className={'dlk-connector-text'}>
                                        <b>Dura Labs Deep Look</b>
                                        <span>Connector</span>
                                    </span>
                                </div>
                                <span className={'dlk-pill'}>✓ Connected</span>
                            </div>
                            <h3>Runs where you work</h3>
                            <p>Claude, ChatGPT, Grok, or anything else that takes an MCP connector.</p>
                        </article>
                    </div>
                </section>

                <section className={'dlk-catches'}>
                    <div className={'dl-wrap'}>
                        <header className={'dlk-head'}>
                            <h2 className={'dl-display'}>What it caught in a real run.</h2>
                            <p>
                                Four failures from one review of a live product, ranked by how much they
                                mattered rather than by how broken the code looked.
                            </p>
                        </header>
                        <div className={'dlk-catch-grid'}>
                            {CATCHES.map(item => (
                                <a className={'dlk-catch'} href={'/deep-look-report'} key={item.title}>
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                    <span className={'dlk-catch-tag'}>{item.tag}</span>
                                    <span className={'dlk-catch-go'}>See it in the report <span className={'dl-arrow'}>→</span></span>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                <section className={'dlk-audience'}>
                    <div className={'dl-wrap dlk-audience-grid'}>
                        <div className={'dlk-audience-say'}>
                            <h2 className={'dl-display'}>For anyone building without a senior engineer.</h2>
                            <ul className={'dlk-chips'}>
                                {AUDIENCE.map(label => <li key={label}>{label}</li>)}
                            </ul>
                            <p>
                                A plan, a half-built product, or something already running. It writes up
                                what it finds so you can hand it to somebody who has never seen this page.
                                Here’s one finding from the real run, exactly as it was written.
                            </p>
                        </div>
                        <ReportPage />
                    </div>
                </section>

                {/* Not a panel. As one it had the same skeleton as the close
                    directly below it, a heading in a left column with the
                    content divided up on the right, so the two stacked as the
                    same shape twice. Plain on the page, the dark close is the
                    only heavy block in this stretch and reads as the finish. */}
                <section className={'dlk-trust'}>
                    <div className={'dl-wrap'}>
                        <header className={'dlk-head'}>
                            <h2 className={'dl-display'}>What you can count on.</h2>
                            <p>The methodology goes out to your agent. Your code stays where it is.</p>
                        </header>
                        <ul className={'dlk-trust-grid'}>
                            {TRUST.map(([title, sub]) => (
                                <li key={title}>
                                    <b>{title}</b>
                                    <span>{sub}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className={'dlk-close'} id={'get'}>
                    <div className={'dl-wrap'}>
                        <div className={'dlk-close-panel'}>
                            <div className={'dlk-close-steps'}>
                                <h2 className={'dl-display'}>Set up in a few minutes.</h2>
                                <ol>
                                    <li><span>1</span>Buy it, signing in with your email</li>
                                    <li><span>2</span>Add the connector in your agent’s settings</li>
                                    <li><span>3</span>Tell your agent to run a deep look</li>
                                </ol>
                            </div>
                            <Closing utils={utils} />
                        </div>
                    </div>
                </section>

                <section className={'dlk-faq'}>
                    <div className={'dl-wrap dlk-faq-inner'}>
                        <h2 className={'dl-display'}>Questions.</h2>
                        {QUESTIONS.map(item => (
                            <details className={'dlk-q'} key={item.q} open={item.open}>
                                <summary>{item.q}</summary>
                                <p>{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>

                <SiteFooter utils={utils} />
            </div>
        </DeepLookStatus>
    )
}

export default DeepLook;
